import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const notificationTypeLabels = {
  proshop30Day: '30-Day Pro Shop',
  memberOpen: 'Member Open',
  memberReminder: 'Member Reminder',
  proshop8Day: '8-Day Pro Shop',
  proshop3Day: '3-Day Pro Shop'
};

const notificationTypeColors = {
  proshop30Day: { bg: '#3b82f6', border: '#2563eb' },
  memberOpen: { bg: '#10b981', border: '#059669' },
  memberReminder: { bg: '#f59e0b', border: '#d97706' },
  proshop8Day: { bg: '#8b5cf6', border: '#7c3aed' },
  proshop3Day: { bg: '#ef4444', border: '#dc2626' }
};

const EmailCalendarView = ({
  pendingNotifications,
  notificationHistory,
  onPreview,
  onSkip,
  onUnskip,
  onTrigger
}) => {
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Navigate to edit page - uses notification ID now
  const handleOpenEdit = (notificationId) => {
    setSelectedEvent(null); // Close the detail popup
    navigate(`/scheduled-emails/edit/${notificationId}`);
  };

  // Transform notification data into calendar events
  // NEW FORMAT: flat notification objects from ScheduledNotification collection
  const calendarEvents = useMemo(() => {
    const events = [];
    const now = new Date();

    // Add pending notifications (from new ScheduledNotification collection)
    pendingNotifications?.forEach(notif => {
      const scheduledDate = new Date(notif.scheduledFor);
      const typeCode = notif.emailTypeCode || notif.emailTypeId?.code || 'unknown';
      const typeName = notif.emailTypeName || notificationTypeLabels[typeCode] || typeCode;
      const eventName = notif.eventName || notif.seasonEventId?.name || 'Unknown Event';

      events.push({
        id: notif._id,
        title: `${typeName}: ${eventName}`,
        start: scheduledDate,
        end: scheduledDate,
        allDay: true,
        resource: {
          notificationId: notif._id,
          eventId: notif.seasonEventId?._id || notif.seasonEventId,
          eventName: eventName,
          eventDate: notif.eventDate || notif.seasonEventId?.eventDate,
          type: typeCode,
          label: typeName,
          scheduledDate: scheduledDate,
          sent: notif.status === 'sent',
          skipped: notif.status === 'skipped',
          skippedReason: notif.skipReason || null,
          isPast: scheduledDate < now,
          status: notif.status
        }
      });
    });

    // Add sent notifications from history (also from ScheduledNotification collection)
    notificationHistory?.forEach(notif => {
      const sentDate = new Date(notif.sentAt || notif.scheduledFor || notif.createdAt);
      const typeCode = notif.emailTypeCode || notif.emailTypeId?.code || 'unknown';
      const typeName = notif.emailTypeName || notificationTypeLabels[typeCode] || typeCode;
      const eventName = notif.eventName || notif.seasonEventId?.name || 'Unknown Event';

      events.push({
        id: `history-${notif._id}`,
        title: `✓ ${typeName}: ${eventName}`,
        start: sentDate,
        end: sentDate,
        allDay: true,
        resource: {
          notificationId: notif._id,
          eventId: notif.seasonEventId?._id || notif.seasonEventId,
          eventName: eventName,
          type: typeCode,
          label: typeName,
          scheduledDate: sentDate,
          sent: true,
          status: notif.status,
          recipientCount: notif.recipientCount,
          error: notif.error,
          isHistory: true
        }
      });
    });

    return events;
  }, [pendingNotifications, notificationHistory]);

  // Custom event styling
  const eventStyleGetter = (event) => {
    const { resource } = event;
    const colors = notificationTypeColors[resource.type] || { bg: '#6b7280', border: '#4b5563' };

    let style = {
      backgroundColor: colors.bg,
      borderColor: colors.border,
      borderWidth: '2px',
      borderStyle: 'solid',
      borderRadius: '4px',
      color: 'white',
      fontSize: '0.75rem',
      padding: '2px 4px',
    };

    if (resource.skipped) {
      style.backgroundColor = '#9ca3af';
      style.borderColor = '#6b7280';
      style.textDecoration = 'line-through';
      style.opacity = 0.7;
    } else if (resource.sent || resource.isHistory) {
      style.opacity = 0.6;
    }

    return { style };
  };

  // Handle event click
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
  };

  // Close modal
  const closeModal = () => {
    setSelectedEvent(null);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <style>{`
        .rbc-calendar {
          font-family: inherit;
        }
        .rbc-header {
          padding: 8px;
          font-weight: 600;
          background: #f8fafc;
        }
        .rbc-month-view {
          border-radius: 0 0 8px 8px;
        }
        .rbc-today {
          background-color: #eff6ff;
        }
        .rbc-event {
          cursor: pointer;
        }
        .rbc-event:hover {
          opacity: 0.9;
        }
        .rbc-toolbar {
          padding: 12px;
          margin-bottom: 0;
        }
        .rbc-toolbar button {
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.875rem;
        }
        .rbc-toolbar button:hover {
          background-color: #f1f5f9;
        }
        .rbc-toolbar button.rbc-active {
          background-color: #3b82f6;
          color: white;
        }
        .rbc-off-range-bg {
          background-color: #f8fafc;
        }
      `}</style>

      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 600 }}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleSelectEvent}
        views={['month', 'week', 'day', 'agenda']}
        defaultView="month"
        popup
        popupOffset={{ x: 0, y: 0 }}
      />

      {/* Legend */}
      <div className="p-4 border-t bg-slate-50 rounded-b-lg">
        <div className="flex flex-wrap gap-3 justify-center">
          {Object.entries(notificationTypeColors).map(([type, colors]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div
                className="w-3 h-3 rounded"
                style={{ backgroundColor: colors.bg }}
              />
              <span className="text-xs text-slate-600">
                {notificationTypeLabels[type]}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gray-400 opacity-70" />
            <span className="text-xs text-slate-600">Skipped</span>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Email Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeModal}
                className="h-8 w-8 p-0"
              >
                ✕
              </Button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-slate-500">Event</p>
                <p className="font-medium">{selectedEvent.resource.eventName}</p>
              </div>

              <div className="flex items-center gap-2">
                <Badge
                  style={{
                    backgroundColor: notificationTypeColors[selectedEvent.resource.type]?.bg || '#6b7280',
                    color: 'white'
                  }}
                >
                  {selectedEvent.resource.label}
                </Badge>
                {selectedEvent.resource.skipped && (
                  <Badge variant="outline" className="bg-gray-100">Skipped</Badge>
                )}
                {selectedEvent.resource.sent && (
                  <Badge variant="outline" className="bg-green-100 text-green-800">Sent</Badge>
                )}
              </div>

              <div>
                <p className="text-sm text-slate-500">Scheduled Date</p>
                <p className="font-medium">{formatDate(selectedEvent.resource.scheduledDate)}</p>
              </div>

              {selectedEvent.resource.eventDate && (
                <div>
                  <p className="text-sm text-slate-500">Event Date</p>
                  <p className="font-medium">{formatDate(selectedEvent.resource.eventDate)}</p>
                </div>
              )}

              {selectedEvent.resource.skippedReason && (
                <div>
                  <p className="text-sm text-slate-500">Skip Reason</p>
                  <p className="text-sm italic text-gray-600">{selectedEvent.resource.skippedReason}</p>
                </div>
              )}

              {selectedEvent.resource.recipientCount > 0 && (
                <div>
                  <p className="text-sm text-slate-500">Recipients</p>
                  <p className="font-medium">{selectedEvent.resource.recipientCount}</p>
                </div>
              )}

              {selectedEvent.resource.error && (
                <div className="bg-red-50 p-3 rounded-md">
                  <p className="text-sm text-red-600">{selectedEvent.resource.error?.message || String(selectedEvent.resource.error)}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {!selectedEvent.resource.isHistory && (
              <div className="flex items-center justify-between p-4 border-t bg-slate-50 rounded-b-lg">
                {/* Edit button on the left */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenEdit(selectedEvent.resource.notificationId)}
                >
                  ✏️ Edit
                </Button>

                {/* Other actions on the right */}
                <div className="flex items-center gap-2">
                  {!selectedEvent.resource.sent && (
                    selectedEvent.resource.skipped ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          onUnskip?.(selectedEvent.resource.notificationId);
                          closeModal();
                        }}
                      >
                        Re-enable
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            onPreview?.(selectedEvent.resource.notificationId);
                            closeModal();
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            onSkip?.(selectedEvent.resource.notificationId);
                            closeModal();
                          }}
                        >
                          Skip
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            onTrigger?.(selectedEvent.resource.notificationId);
                            closeModal();
                          }}
                        >
                          Send Now
                        </Button>
                      </>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCalendarView;
